import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import LoadingOverlay from 'react-loading-overlay';
import { IoMdClose } from "react-icons/io";
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import moment from 'moment';
import classNames from "classnames";

// Custom Component
import { PrimaryButton } from '../Button';
import PlayerForm from '../Form/PlayerForm';
import ErrorText from '../TextField/ErrorText';
import SuccessAlert from '../Alert/SuccessAlert';
import ErrorAlert from '../Alert/ErrorAlert';

// Utils
import { convertEventType, convertAgeGroup, getPlayerCount } from '../../utils/helper';

// API Service
import { addPlayerService } from '../../service/tournament.service';
import { getOrganizerTShirtService } from '../../service/tshirt.service';

// Redux-Functions
import { selectUser } from '../../redux/userSlice';

const AddPlayerModal = (props) => {
    const user = useSelector(selectUser);

    const [tShirtList, setTShirtList] = useState([]);
    const [loading, setLoading] = useState(false);
    LoadingOverlay.propTypes = undefined

    const { register, control, watch, setValue, reset, handleSubmit, formState: { errors } } = useForm({
        mode: "all",
        defaultValues: {
            player_details: [
                {
                    player_name: "",
                    club_name: "",
                    player_email: "",
                    player_contact: "",
                    player_gender: "MALE",
                    player_dob: "",
                    player_idcard: "",
                    nationality: "",
                    ...(props?.tournament?.sports_type === "PICKLEBALL" && {
                        dupr_rating: ""
                    })
                }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "player_details"
    });

    const watchEventType = watch("event_type")
    const allWatchedValues = watch({
        nest: true
    });

    useEffect(() => {
        if (watchEventType && props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0) {
            if ((watchEventType !== "JUNIOR_SINGLE" && watchEventType !== "GROUP_CAPTAIN") && allWatchedValues?.player_details?.length > getPlayerCount(watchEventType)) {
                setValue('player_details', allWatchedValues?.player_details?.slice(0, getPlayerCount(watchEventType)));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.tournament, watchEventType])

    const reactSelectCustomStyle = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: state.isFocused && "1px solid #FA5000",
            boxShadow: state.isFocused && "0px 0px 2px #FA5000",
            "&:hover": {
                border: "1px solid #FA5000",
                boxShadow: "0px 0px 2px #FA5000"
            }
        }),
        option: (styles, { isSelected }) => {
            return {
                ...styles,
                backgroundColor: isSelected ? '#FA5000' : 'white',
                "&:hover": {
                    backgroundColor: "#FA5000",
                    color: 'white'
                }
            }
        },
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#CCCCCC'
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#CCCCCC'
        })
    }

    /**
        * @function fetchOrganizerTShirt
        * @params
        * @description fetch the single tournament details for organizer
    */
    const fetchOrganizerTShirt = async () => {
        try {
            const result = await getOrganizerTShirtService({
                id: props?.tournament?.id
            });
            if (result?.data?.status) {
                setTShirtList(result?.data?.data)
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
        } catch (error) {
            ErrorAlert(error)
        }
    }

    useEffect(() => {
        if (props?.tournament?.id && user?.type === 'organizer') {
            fetchOrganizerTShirt()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, user])

    /**
        * @function onSubmit
        * @params formdata
        * @description used to add player from organizer
    */
    const onSubmit = async (formdata) => {
        if (formdata?.event_type && props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0) {
            if ((formdata?.event_type !== "JUNIOR_SINGLE" && formdata?.event_type !== "GROUP_CAPTAIN") && formdata?.player_details?.length !== getPlayerCount(formdata?.event_type)) {
                ErrorAlert(`You must add ${getPlayerCount(formdata?.event_type)} player for ${convertEventType(formdata?.event_type)} Event`);
                return
            }
        }
        const data = new FormData()
        data.append('tournament_id', props?.tournament?.id)
        if (formdata?.player_details?.length > 0) {
            formdata?.player_details?.forEach((item, index) => {
                data.append(`player_details[${index}][event_type]`, formdata?.event_type === "JUNIOR_SINGLE" ? "GROUP_CAPTAIN" : formdata?.event_type)
                data.append(`player_details[${index}][age_group]`, formdata?.event_type_age_group_value?.value?.split("|")[1])
                data.append(`player_details[${index}][age_value]`, formdata?.event_type_age_group_value?.value?.split("|")[2])
                if (item?.player_name) {
                    data.append(`player_details[${index}][player_name]`, item?.player_name)
                }
                if (item?.club_name) {
                    data.append(`player_details[${index}][club_name]`, item?.club_name)
                }
                if (item?.player_email) {
                    data.append(`player_details[${index}][player_email]`, item?.player_email)
                }
                if (item?.player_contact) {
                    data.append(`player_details[${index}][player_contact]`, item?.player_contact)
                }
                if (item?.player_gender) {
                    data.append(`player_details[${index}][player_gender]`, item?.player_gender)
                }
                if (item?.player_dob) {
                    data.append(`player_details[${index}][player_dob]`, moment(item?.player_dob).format("YYYY-MM-DD"))
                }
                if (item?.player_idcard) {
                    data.append(`player_details[${index}][player_idcard]`, item?.player_idcard)
                }
                if (item?.nationality) {
                    data.append(`player_details[${index}][nationality]`, item?.nationality)
                }
                if (item?.dupr_rating && props?.tournament?.sports_type === "PICKLEBALL") {
                    data.append(`player_details[${index}][dupr_rating]`, item?.dupr_rating)
                }
                data.append(`player_details[${index}][proffesional_player]`, item?.proffesional_player ? 1 : 0)
                if (item?.tshirt_id?.value) {
                    data.append(`player_details[${index}][tshirt_details][tshirt_id]`, item?.tshirt_id?.value)
                    data.append(`player_details[${index}][tshirt_details][tshirt_size_id]`, item?.tshirt_size_id?.value)
                }
            })
        }
        try {
            setLoading(true)
            const result = await addPlayerService(data);
            if (result?.data?.status) {
                SuccessAlert(result?.data?.message);
                props?.setCurrentPage(0)
                await props?.fetchOrganizerPlayerList(1);
                await props?.fetchAllOrganizerPlayerList();
                props.handleClose();
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function handleClose
        * @params
        * @description used to close add player modal
    */
    const handleClose = async () => {
        reset()
        props.handleClose()
    }

    return <Modal show={props.show} onHide={handleClose} centered size="lg">
        <LoadingOverlay
            active={loading}
            spinner={<Spinner animation="border" />}
        >
            <Modal.Header className='fs-24 fw-600 border-0' closeButton>
                Add Player
            </Modal.Header>
            <Modal.Body className='d-flex flex-column pt-0 align-items-center'>
                <div className='w-100'>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <span className='fs-20 fw-600'>Players</span>
                            <div role='button' className={classNames('bg-danger bg-opacity-10 rounded py-1 px-3', {
                                "d-none": (props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.length > 0 && getPlayerCount(allWatchedValues?.event_type) === allWatchedValues?.player_details?.length) || !allWatchedValues?.event_type
                            })} onClick={() => {
                                append({
                                    player_name: "",
                                    club_name: "",
                                    player_email: "",
                                    player_contact: "",
                                    player_gender: "MALE",
                                    player_dob: "",
                                    player_idcard: "",
                                    nationality: "",
                                    ...(props?.tournament?.sports_type === "PICKLEBALL" && {
                                        dupr_rating: ""
                                    })
                                })
                            }}>
                                <span className='text-primary fw-bold'>+ Add Player</span>
                            </div>
                        </div>
                        <Row>
                            <Col xs={12} lg={6}>
                                <Form.Group className="my-4">
                                    <Form.Label className='fs-14 fw-500'>Select Event</Form.Label>
                                    <Controller
                                        name={`event_type_age_group_value`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                styles={reactSelectCustomStyle}
                                                placeholder='Select Event'
                                                options={props?.tournament?.event_details?.filter(item => !item?.team_event_id)?.map((item) => {
                                                    return {
                                                        value: `${item?.event_type}|${item?.age_group}|${item?.age_value}`,
                                                        label: `${convertEventType(item?.event_type)} (${convertAgeGroup(item?.age_group, item?.age_value)})`,
                                                        isDisabled: item?.is_disabled || item?.is_category_full
                                                    }
                                                })}
                                                isSearchable={true}
                                                {...field}
                                                onChange={(value) => {
                                                    setValue("event_type_age_group_value", value)
                                                    setValue(`event_type`, value?.value?.split("|")[0])
                                                }}
                                            />
                                        )}
                                        rules={{
                                            required: 'Event Type is required'
                                        }}
                                    />
                                    {errors?.event_type_age_group_value && <ErrorText>{errors?.event_type_age_group_value?.message}</ErrorText>}
                                </Form.Group>
                            </Col>
                        </Row>
                        {fields.map((item, index) => {
                            return <div className='mb-3 px-3 py-2 border rounded' key={item?.id}>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <span className='fs-20 fw-600'>Player - {index + 1}</span>
                                    {allWatchedValues?.player_details?.length > 1 && <IoMdClose role='button' color='red' size={20} onClick={() => remove(index)} />}
                                </div>
                                <PlayerForm
                                    playerIndex={index}
                                    tournament={props?.tournament}
                                    tShirtList={tShirtList}
                                    {...{ control, register, setValue, errors }}
                                />
                            </div>
                        })}
                        <div className='d-flex justify-content-end align-items-end mt-4'>
                            <div>
                                <PrimaryButton className='fs-14 fw-500 px-5' disabled={loading} loading={loading} type="submit">Add Player</PrimaryButton>
                            </div>
                        </div>
                    </Form>
                </div>
            </Modal.Body>
        </LoadingOverlay>
    </Modal>
};

export default AddPlayerModal;